import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { FlickityDirective } from "ngx-flickity";

import { isPlatformServer } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from "@angular/core";
// For Flickity
import { Injectable, PLATFORM_ID } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";

@Injectable()
export class AppConfigService {
  private platformId = inject(PLATFORM_ID);

  isPlatformServer() {
    return isPlatformServer(this.platformId);
  }
}

interface OnboardingScreen {
  imageUrl: Array<string>;
  index: number;
  title: string;
  content: string;
  isFirst?: boolean;
  isLast?: boolean;
}

const SLIDES = [
  { titleKey: "Onboarding.Slide89_1.Title", contentKey: "Onboarding.Slide89_1.Content" },
  { titleKey: "Onboarding.Slide89_2.Title", contentKey: "Onboarding.Slide89_2.Content" },
  { titleKey: "Onboarding.Slide89_3.Title", contentKey: "Onboarding.Slide89_3.Content" },
  { titleKey: "Onboarding.Slide89_4.Title", contentKey: "Onboarding.Slide89_4.Content" },
  { titleKey: "Onboarding.Slide89_5.Title", contentKey: "Onboarding.Slide89_5.Content" },
];

@Component({
  selector: "dialog-onboarding",
  templateUrl: "dialog-onboarding.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, TranslatePipe],
})
export class DialogOnboarding implements AfterViewInit {
  protected dialogRef = inject(MatDialogRef<DialogOnboarding>);
  protected translate = inject(TranslateService);

  done = output();

  private flickity = viewChild.required<ElementRef>("flickityelement");

  private readonly activeSlide = signal(0);
  protected readonly dotInfo = computed(() => this.screens.map((_, i) => ({ active: i === this.activeSlide() })));
  protected neverAgain = false;
  protected screens: OnboardingScreen[] = SLIDES.map((slide, i) => ({
    index: i,
    imageUrl: [`assets/images/onboarding/visual-slide89_${i + 1}@2x.png`],
    title: this.translate.instant(slide.titleKey),
    content: this.translate.instant(slide.contentKey),
    isFirst: i === 0,
    isLast: i === SLIDES.length - 1,
  }));

  private flickityElement: FlickityDirective | null = null;

  protected closeDialog() {
    this.dialogRef.close();
    if (this.neverAgain) {
      this.done.emit();
    }
  }

  protected markAsCompleted() {
    if (this.neverAgain) {
      this.done.emit();
    }
    this.dialogRef.close();
  }

  protected flickityClick(index: number) {
    if (this.flickityElement) {
      this.flickityElement.select(index, false, false);
      this.activeSlide.set(this.flickityElement.selectedIndex);
    }
  }

  ngAfterViewInit() {
    const acs: AppConfigService = new AppConfigService();
    this.flickityElement = new FlickityDirective(this.flickity(), acs as any);
    this.flickityElement.config = {
      cellAlign: "left",
      imagesLoaded: true,
      pageDots: false,
      prevNextButtons: false,
      draggable: false,
    };
    this.flickityElement.init();
    setTimeout(() => {
      this.activeSlide.set(this.flickityElement?.selectedIndex ?? 0);
    });
  }

  protected flickityNext() {
    if (this.flickityElement) {
      this.flickityElement.next();
      this.activeSlide.set(this.flickityElement.selectedIndex);
    }
  }
}
